import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	PetitionDetailsSpikingPetition,
	type PetitionDetailsSpikingPetitionQuery,
	type PetitionDetailsSpikingPetitionQueryVariables,
} from './spikingPetition.graphql';

export type SpikingPetition = Readonly<{
	id: string;
	slug: string;
	displayTitle: string;
	// Cached signature count for the past day.
	signatureCount: number;
}>;

export type SpikingPetitionLookup = Readonly<{
	petition: SpikingPetition;
	// Canonical id of the petition being viewed. Resolved by the same query, the same way the page resolves
	// its route param, so an alternate slug or id format still matches.
	currentPetitionId: string | undefined;
}>;

/**
 * Returns the spiking petition (with the current page's petition id), or null when it doesn't exist, isn't PUBLISHED, or the fetch fails.
 * Never throws: the banner is optional, so a failure here must not break the petition page.
 */
export async function getSpikingPetition(
	{ gql: { fetch }, errorReporter }: UtilityContext,
	{ petitionId, currentSlugOrId }: { petitionId: number; currentSlugOrId: string },
): Promise<SpikingPetitionLookup | null> {
	try {
		const { data } = await fetch<PetitionDetailsSpikingPetitionQuery, PetitionDetailsSpikingPetitionQueryVariables>({
			query: PetitionDetailsSpikingPetition,
			variables: { slugOrId: String(petitionId), currentSlugOrId },
			rejectOnError: true,
		});

		const { petition, currentPetition } = data;
		if (!petition || petition.status !== 'PUBLISHED') return null;

		return {
			petition: {
				id: petition.id,
				slug: petition.slug,
				displayTitle: petition.displayTitle,
				signatureCount: petition.dailySignatureCount,
			},
			currentPetitionId: currentPetition?.id,
		};
	} catch (error) {
		void errorReporter.report({
			error: error instanceof Error ? error : new Error('spiking_petition_banner_fetch_failed', { cause: error }),
		});
		return null;
	}
}
